/**
 * Sprint V100+ · KlickTipp Danke-Seite (Post-DOI Willkommens-Landing).
 * Route: /webinar/willkommen?subscriberId=…&listId=…&subscriberKey=…
 *
 * Zeigt: „Du bist offiziell dabei" + Webinar-Termin + ICS-Download +
 * Wegweiser (Cockpit-Login sobald verfügbar). Datensparsam: keine
 * Klartext-E-Mail in URL, nur subscriberKey (verschlüsselter KT-ID).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, Sparkles, MailCheck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmtDateDe(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const p = fmt.formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    return `${p.weekday}, ${p.day}.${p.month}.${p.year} · ${p.hour}:${p.minute} Uhr`;
  } catch {
    return iso;
  }
}

function buildIcsDownload(config) {
  if (!config?.event_datetime) return null;
  const dtStart = new Date(config.event_datetime);
  const dtEnd = new Date(dtStart.getTime() + (config.duration_minutes || 90) * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Flex4Success//Webinar//DE",
    "BEGIN:VEVENT",
    `UID:webinar-${config.webinar_id}@flex4success`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(dtStart)}`,
    `DTEND:${fmt(dtEnd)}`,
    `SUMMARY:${config.title}`,
    `DESCRIPTION:${(config.subtitle || "").replace(/,/g, "\\,")}`,
    `URL:${config.join_url || ""}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

export default function WebinarWillkommen() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const subscriberKey = params.get("subscriberKey") || "";
  const subscriberId = params.get("subscriberId") || "";
  const [config, setConfig] = useState(null);

  // V100+ Iteration 3 (18.09.2026) — Auto-Login-Handoff (idempotent).
  // Wenn diese Seite direkt als KT-Bestätigungsseite konfiguriert ist
  // (statt /webinar/doi-bestätigt), muss auch hier der Handoff laufen,
  // damit `magic_link_recovery_pending` gecleart wird und keine zweite
  // Mail feuert. Fire-and-forget; Fehler blockieren die UX nicht.
  useEffect(() => {
    const k = (subscriberKey || subscriberId || "").trim();
    if (!k) return;
    (async () => {
      try {
        await api.post("/auth/doi/handoff", { k });
        try { await refresh(); } catch { /* silent */ }
      } catch { /* silent — recovery-scheduler bleibt als Fallback */ }
    })();
  }, [subscriberKey, subscriberId, refresh]);

  // Aktuelles/nächstes Webinar aus DB (dynamisches Seeding via V98.6).
  useEffect(() => {
    api
      .get(`/webinar/1`)
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  const dateDe = useMemo(
    () => (config ? fmtDateDe(config.event_datetime) : ""),
    [config],
  );
  const icsUrl = useMemo(() => buildIcsDownload(config), [config]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center px-4 py-12"
      data-testid="webinar-willkommen"
    >
      <div className="max-w-xl w-full">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-[#F3651D] to-[#4563BB] flex items-center justify-center mb-6 shadow-lg">
            <Sparkles className="w-12 h-12 text-white" />
          </div>
          <p className="text-xs uppercase tracking-widest text-[#F3651D] font-bold mb-2">
            Willkommen an Bord
          </p>
          <h1
            className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white leading-tight"
            data-testid="webinar-willkommen-headline"
          >
            Du bist offiziell dabei.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
            Dein Platz im Live-Webinar ist reserviert. Ich freu mich auf dich.
          </p>
        </div>

        {config && (
          <div
            className="mt-8 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 sm:p-7 shadow-sm"
            data-testid="webinar-willkommen-card"
          >
            <p className="text-xs uppercase tracking-widest text-[#F3651D] font-bold">
              Dein Termin
            </p>
            <p
              className="mt-2 text-xl font-bold text-slate-900 dark:text-white"
              data-testid="webinar-willkommen-title"
            >
              {config.title}
            </p>
            {config.subtitle && (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {config.subtitle}
              </p>
            )}
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                Wann
              </p>
              <p
                className="mt-1 text-lg font-semibold text-slate-800 dark:text-slate-100"
                data-testid="webinar-willkommen-date"
              >
                {dateDe}
              </p>
            </div>
            {icsUrl && (
              <a
                href={icsUrl}
                download={`webinar-${config.webinar_id}.ics`}
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#4563BB] hover:bg-[#3a54a3] text-white px-5 py-2.5 text-sm font-semibold transition-colors shadow-sm"
                data-testid="webinar-willkommen-ics"
              >
                <CalendarCheck className="w-4 h-4" />
                In Kalender speichern
              </a>
            )}
          </div>
        )}

        <div className="mt-6 rounded-xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-start gap-3">
            <MailCheck className="w-5 h-5 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                So geht's weiter
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Kurz vor dem Webinar bekommst du eine Erinnerung mit dem Zugangs-Link
                per E-Mail. Du kannst dich außerdem jederzeit im{" "}
                <a
                  href="/login"
                  className="text-[#4563BB] dark:text-[#7f9be0] font-semibold hover:underline"
                  data-testid="webinar-willkommen-login-link"
                >
                  Mitgliederbereich einloggen
                </a>{" "}
                — dort findest du alle Details.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid="webinar-willkommen-home-link"
          >
            Zur Startseite
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {subscriberKey && (
          <p className="mt-4 text-center text-[10px] text-slate-400 dark:text-slate-600 font-mono">
            Ref: {subscriberKey.slice(0, 8)}…
          </p>
        )}
      </div>
    </div>
  );
}
